/**
 * Quick view helpers (prototype `ProductQuickView`), applied to the content Horizon's quick add
 * puts in #quick-add-modal-content (and re-applied after the variant picker is re-rendered):
 * - options with a single value are hidden;
 * - non-color options get a native <select> for phones that drives the hidden buttons, so
 *   Horizon's picker, the option priority rule and the add button keep working unchanged.
 */

/** @param {HTMLElement} fieldset */
function enhanceOption(fieldset) {
  const inputs = /** @type {HTMLInputElement[]} */ ([...fieldset.querySelectorAll('input[type="radio"]')]);
  if (!inputs.length) return;

  const single = inputs.length < 2;
  if (fieldset.hasAttribute('data-hl-single') !== single) fieldset.toggleAttribute('data-hl-single', single);
  const name = (inputs[0]?.dataset.optionName ?? '').trim();
  if (single || fieldset.querySelector('.hl-color-option')) return;

  // Rebuild the select only when the options' state changed, so this never loops on its own edits.
  const signature = inputs
    .map((input) => `${input.value}|${input.checked ? 1 : 0}|${input.dataset.optionAvailable}`)
    .join(';');
  let select = /** @type {HTMLSelectElement | null} */ (fieldset.querySelector(':scope > .hl-qv-select'));
  if (select?.dataset.signature === signature) return;

  if (!select) {
    select = document.createElement('select');
    select.className = 'hl-qv-select';
    select.setAttribute('aria-label', fieldset.querySelector('legend')?.textContent?.trim() || name);
    select.addEventListener('change', (event) => {
      // Keep the mirror's own change away from Horizon's picker; the matching button click drives it.
      event.stopPropagation();
      const wanted = [...fieldset.querySelectorAll('input[type="radio"]')].find(
        (input) => input instanceof HTMLInputElement && input.value === select?.value
      );
      if (wanted instanceof HTMLInputElement && !wanted.checked) wanted.click();
    });
    const legend = fieldset.querySelector('legend');
    legend ? legend.after(select) : fieldset.prepend(select);
  }

  select.dataset.signature = signature;
  select.replaceChildren(
    ...inputs.map((input) => {
      // Selected as a property only: Horizon's picker reads `option[selected]` attributes as its own.
      const option = new Option(input.value, input.value, false, input.checked);
      option.disabled = input.dataset.optionAvailable === 'false' && !input.checked;
      return option;
    })
  );
}

function enhance() {
  const content = document.getElementById('quick-add-modal-content');
  if (!content) return;
  for (const fieldset of content.querySelectorAll('variant-picker .variant-option')) {
    if (fieldset instanceof HTMLElement) enhanceOption(fieldset);
  }
}

const content = document.getElementById('quick-add-modal-content');
if (content) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timer;
  new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(enhance, 0);
  }).observe(content, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'data-option-available'] });
  enhance();
}
